/**
 * App & web visibility logic (see README): adds state classes like `.isApp` to elements with the matching `.changeOn…` class, based on the app params.
 * The apps add some of them in `photoneAppInit()` too.
 */

import {getOsFromUserAgent} from '../lib/dom.ts'
import {CLIENT_KEY, getParameter, MEDIUM_PARAM_KEY, PRO_KEY, SENSOR_KEY, VERSION_KEY} from '../lib/params.ts'
import {isVersionNewerOrEqual} from '../lib/version.ts'

/**
 * App versions that show/hide content via `.changeOnVersion` and classes like `.hide-on-android-version-greater-0-6-0` (see hide-show-logic.scss):
 * - 0.6.0: hides "Pro Upsell" in LS guide on Android, as LS are free but Pro exists
 */
const VERSIONS_BY_OS: Record<string, string[]> = {android: ['0.6.0'], ios: []}

/**
 * Maps the slugs of translated Pro guides to the English one, which the upgrade buttons pass to the apps (`data-title`), so analytics know which
 * guide the upgrade screen was opened from. It's an unfortunate HACK, but that's still the simplest way we found.
 */
const PRO_GUIDES_SLUGS: Record<string, string[]> = {
    'cannabis-lighting-guide': ['beleuchtungsanleitung-fur-cannabis'],
    'houseplant-lighting-guide': ['beleuchtungsanleitung-fur-zimmerpflanzen'],
    'coral-reef-lighting-guide': ['beleuchtung-fur-korallen'],
}

export function applyVisibilityClasses() {
    mapUpgradeButtonsToEnglishGuides()
    if (!getParameter(PRO_KEY)) document.querySelectorAll('.changeOnPro').forEach(element => element.remove()) // no Pro feature (Android Beta, website)

    // Change sensor specific logic only if sensor param is actually provided. If not, the existent (default) state should be left as is.
    const sensor = getParameter(SENSOR_KEY)
    if (sensor) {
        document.querySelectorAll('.changeOnSensor').forEach(element => {
            element.classList.toggle('isAls', sensor === 'ALS')
            element.classList.toggle('isCam', sensor === 'CAM')
        })
    }

    const isApp = getParameter(CLIENT_KEY) === 'app'
    document.querySelectorAll('.changeOnApp').forEach(element => element.classList.toggle('isApp', isApp))

    hideStoreBadgeOfOtherOs()
    changeOnVersion()
}

function mapUpgradeButtonsToEnglishGuides() {
    document.querySelectorAll<HTMLElement>('.guideUpgradeListener.btn').forEach(button => {
        const title = button.dataset.title
        if (!title) return
        const enSlug = Object.keys(PRO_GUIDES_SLUGS).find(slug => PRO_GUIDES_SLUGS[slug].includes(title))
        if (enSlug) button.dataset.title = enSlug
    })
}

/** Shows only the App Store badges on iOS, only the Google Play ones on Android, and both elsewhere */
function hideStoreBadgeOfOtherOs() {
    const os = getOsFromUserAgent()
    const hiddenSelector = {ios: '.cta-android', android: '.cta-ios', other: null}[os]
    if (hiddenSelector) document.querySelectorAll<HTMLElement>(hiddenSelector).forEach(element => element.style.display = 'none')
}

/** Adds e.g. `.isAndroidVersionGreater0-6-0` to `.changeOnVersion` elements if the app's version is newer than or equal to that version */
function changeOnVersion() {
    const currentOs = getParameter(MEDIUM_PARAM_KEY) // Photone reports "android" or "ios" in utm_medium param
    const isCurrentOsSupported = currentOs != null && Object.keys(VERSIONS_BY_OS).includes(currentOs)
    const appVersion = getParameter(VERSION_KEY)

    document.querySelectorAll('.changeOnVersion').forEach(element => {
        for (const [os, versions] of Object.entries(VERSIONS_BY_OS)) {
            for (const version of versions) {
                const dashedVersion = version.replaceAll('.', '-')
                if (isCurrentOsSupported && currentOs !== os) {
                    // Remove stuff from other OS as it would just interfere
                    element.classList.remove(`hide-on-${os}-version-greater-${dashedVersion}`, `show-on-${os}-version-greater-${dashedVersion}`)
                    continue
                }

                const isNewer = !!appVersion && isVersionNewerOrEqual(appVersion, version) // an empty version param isn't a version
                const shouldDisplay = isNewer || !isCurrentOsSupported // no/unsupported OS param = show the newest version
                element.classList.toggle(`is${os[0].toUpperCase()}${os.slice(1)}VersionGreater${dashedVersion}`, shouldDisplay)
            }
        }
    })
}
